import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import LogoLoader from '../LogoLoader';
import axios from 'axios';

// Styled Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f9fbff 0%, #ffffff 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const PaymentCard = styled.div`
  background: linear-gradient(135deg, rgba(244, 245, 249, 0.95) 0%, rgba(244, 245, 248, 0.95) 100%);
  border: 1px solid rgba(91, 97, 234, 0.3);
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(39, 68, 131, 0.14);
`;

const Header = styled.div`
  background: linear-gradient(135deg, rgba(18, 165, 113, 0.15) 0%, rgba(15, 154, 104, 0.15) 100%);
  padding: 24px;
  text-align: center;
  border-bottom: 1px solid rgba(18, 165, 113, 0.2);
`;

const ExpertAvatar = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, #12a571 0%, #0f9a68 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
  font-size: 24px;
  font-weight: 600;
  color: #fff;
  
  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
  }
`;

const ExpertName = styled.h2`
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 600;
  color: #192947;
`;

const ExpertSubtitle = styled.p`
  margin: 0;
  font-size: 13px;
  color: #65748d;
`;

const Body = styled.div`
  padding: 24px;
`;

const AmountSection = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const AmountLabel = styled.p`
  margin: 0 0 8px;
  font-size: 13px;
  color: #65748d;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const Amount = styled.div`
  font-size: 40px;
  font-weight: 700;
  color: #0f8f63;
`;

const Description = styled.div`
  background: rgba(25, 41, 71, 0.04);
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
`;

const DescriptionLabel = styled.p`
  margin: 0 0 6px;
  font-size: 12px;
  color: #7b8aa3;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DescriptionText = styled.p`
  margin: 0;
  font-size: 15px;
  color: #192947;
  line-height: 1.5;
`;

const ExpiryNotice = styled.div`
  background: rgba(239, 155, 97, 0.1);
  border: 1px solid rgba(239, 155, 97, 0.3);
  border-radius: 8px;
  padding: 12px 16px;
  margin-bottom: 24px;
  font-size: 13px;
  color: #a2611c;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaymentOptionsTitle = styled.h3`
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 600;
  color: #192947;
`;

const PaymentOption = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(91, 97, 234, 0.1);
  border: 1px solid rgba(91, 97, 234, 0.2);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  text-decoration: none;
  color: #192947;
  transition: all 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: rgba(91, 97, 234, 0.2);
    border-color: rgba(91, 97, 234, 0.4);
    transform: translateY(-2px);
  }
`;

const PaymentOptionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const PaymentIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$bg || 'rgba(91, 97, 234, 0.2)'};
  font-size: 18px;
`;

const PaymentLabel = styled.span`
  font-weight: 500;
  font-size: 15px;
`;

const PaymentArrow = styled.span`
  font-size: 18px;
  color: #7b8aa3;
`;

const BankDetails = styled.div`
  background: rgba(25, 41, 71, 0.06);
  border-radius: 10px;
  padding: 16px;
  margin-top: 8px;
`;

const BankRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #edf1f9;
  
  &:last-child {
    border-bottom: none;
  }
`;

const BankLabel = styled.span`
  font-size: 13px;
  color: #7b8aa3;
`;

const BankValue = styled.span`
  font-size: 13px;
  color: #192947;
  font-family: monospace;
`;

const ConfirmSection = styled.div`
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #edf1f9;
`;

const ConfirmButton = styled.button`
  width: 100%;
  padding: 14px;
  background: ${props => props.$confirmed 
    ? 'linear-gradient(135deg, #12a571 0%, #0f9a68 100%)' 
    : '#edf2ff'};
  border: 1px solid ${props => props.$confirmed 
    ? 'rgba(18, 165, 113, 0.5)' 
    : '#e0e8f5'};
  border-radius: 10px;
  color: #192947;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-1px);
    ${props => props.$confirmed && 'box-shadow: 0 4px 12px rgba(18, 165, 113, 0.4);'}
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 600;
  background: ${props => {
    switch(props.$status) {
      case 'paid': return 'rgba(18, 165, 113, 0.2)';
      case 'expired': return 'rgba(229, 72, 77, 0.2)';
      case 'cancelled': return '#65748d';
      default: return 'rgba(239, 155, 97, 0.2)';
    }
  }};
  color: ${props => {
    switch(props.$status) {
      case 'paid': return '#0f8f63';
      case 'expired': return '#e5484d';
      case 'cancelled': return '#7b8aa3';
      default: return '#ef9b61';
    }
  }};
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: 40px;
  
  h2 {
    color: #d63c3c;
    margin-bottom: 12px;
  }
  
  p {
    color: #65748d;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  text-align: center;
  padding: 60px;
  color: #65748d;
`;

const Logo = styled.div`
  margin-bottom: 24px;
  text-align: center;
  
  img {
    height: 40px;
  }
  
  span {
    font-size: 24px;
    font-weight: 700;
    background: linear-gradient(135deg, #5b61ea 0%, #885de8 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
  }
`;

// Payment method icons
const getPaymentIcon = (method) => {
  switch (method) {
    case 'paypal': return '💳';
    case 'bit': return '📱';
    case 'bank_transfer': return '🏦';
    case 'custom': return '🔗';
    default: return '💰';
  }
};

const getPaymentBg = (method) => {
  switch (method) {
    case 'paypal': return 'rgba(0, 112, 186, 0.2)';
    case 'bit': return 'rgba(46, 204, 113, 0.2)';
    case 'bank_transfer': return 'rgba(155, 89, 182, 0.2)';
    case 'custom': return 'rgba(241, 196, 15, 0.2)';
    default: return 'rgba(91, 97, 234, 0.2)';
  }
};

const formatCurrency = (amount, currency) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD'
  }).format(amount);
};

const PaymentPage = () => {
  const { shortCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentRequest, setPaymentRequest] = useState(null);
  const [expert, setExpert] = useState(null);
  const [expandedBank, setExpandedBank] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);

  useEffect(() => {
    loadPaymentRequest();
  }, [shortCode]);

  const loadPaymentRequest = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_MAVEN_SERVER_URL}/client-payments/pay/${shortCode}`
      );
      setPaymentRequest(response.data.paymentRequest);
      setExpert(response.data.expert);
    } catch (err) {
      console.error('Failed to load payment request:', err);
      setError(err.response?.data?.error || 'Payment request not found');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!selectedMethod) return;
    
    try {
      setConfirming(true);
      await axios.post(
        `${process.env.REACT_APP_MAVEN_SERVER_URL}/client-payments/pay/${shortCode}`,
        { paidVia: selectedMethod }
      );
      // Reload to show updated status
      await loadPaymentRequest();
    } catch (err) {
      console.error('Failed to confirm payment:', err);
    } finally {
      setConfirming(false);
    }
  };

  const handlePaymentOptionClick = (option) => {
    if (option.paymentLink) {
      // Open payment link in new tab
      window.open(option.paymentLink, '_blank', 'noopener,noreferrer');
    }
    setSelectedMethod(option.method);
    if (option.method === 'bank_transfer') {
      setExpandedBank(true);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <PaymentCard>
          <LoadingContainer>
            <LogoLoader size={160} message="Loading payment details" />
          </LoadingContainer>
        </PaymentCard>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <PaymentCard>
          <ErrorContainer>
            <h2>😕 Oops!</h2>
            <p>{error}</p>
          </ErrorContainer>
        </PaymentCard>
      </PageContainer>
    );
  }

  const isExpired = new Date(paymentRequest.expiresAt) < new Date();
  const isPaid = paymentRequest.status === 'paid';
  const isCancelled = paymentRequest.status === 'cancelled';
  const isActive = !isPaid && !isCancelled && !isExpired;

  return (
    <PageContainer>
      <Logo>
        <span>iMaven</span>
      </Logo>
      
      <PaymentCard>
        <Header>
          <ExpertAvatar>
            {expert?.imageUrl ? (
              <img src={expert.imageUrl} alt={expert.name} />
            ) : (
              expert?.name?.charAt(0) || '?'
            )}
          </ExpertAvatar>
          <ExpertName>{expert?.name || 'Professional'}</ExpertName>
          <ExpertSubtitle>Payment Request</ExpertSubtitle>
        </Header>

        <Body>
          {/* Status Badge */}
          {!isActive && (
            <div style={{ textAlign: 'center', marginBottom: 24 }}>
              <StatusBadge $status={isPaid ? 'paid' : isCancelled ? 'cancelled' : 'expired'}>
                {isPaid ? '✓ Payment Confirmed' : isCancelled ? '✕ Cancelled' : '⏱ Expired'}
              </StatusBadge>
            </div>
          )}

          {/* Amount */}
          <AmountSection>
            <AmountLabel>Amount Due</AmountLabel>
            <Amount>{formatCurrency(paymentRequest.amount, paymentRequest.currency)}</Amount>
          </AmountSection>

          {/* Description */}
          <Description>
            <DescriptionLabel>For</DescriptionLabel>
            <DescriptionText>{paymentRequest.description}</DescriptionText>
            {paymentRequest.serviceType && (
              <div style={{ marginTop: 8 }}>
                <span style={{ 
                  fontSize: 12, 
                  padding: '4px 8px', 
                  background: 'rgba(91, 97, 234, 0.2)', 
                  borderRadius: 4,
                  color: '#5874be'
                }}>
                  {paymentRequest.serviceType}
                </span>
              </div>
            )}
          </Description>

          {/* Expiry Notice */}
          {isActive && (
            <ExpiryNotice>
              ⏱ Expires: {new Date(paymentRequest.expiresAt).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </ExpiryNotice>
          )}

          {/* Payment Options - only show if active */}
          {isActive && (
            <>
              <PaymentOptionsTitle>Choose Payment Method</PaymentOptionsTitle>
              
              {paymentRequest.paymentOptions?.map((option, index) => (
                <div key={index}>
                  <PaymentOption
                    as={option.paymentLink ? 'a' : 'div'}
                    href={option.paymentLink || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => {
                      if (!option.paymentLink) e.preventDefault();
                      handlePaymentOptionClick(option);
                    }}
                    style={{ 
                      borderColor: selectedMethod === option.method ? 'rgba(18, 165, 113, 0.5)' : undefined,
                      background: selectedMethod === option.method ? 'rgba(18, 165, 113, 0.1)' : undefined
                    }}
                  >
                    <PaymentOptionInfo>
                      <PaymentIcon $bg={getPaymentBg(option.method)}>
                        {getPaymentIcon(option.method)}
                      </PaymentIcon>
                      <PaymentLabel>{option.label}</PaymentLabel>
                    </PaymentOptionInfo>
                    <PaymentArrow>→</PaymentArrow>
                  </PaymentOption>

                  {/* Bank Transfer Details */}
                  {option.method === 'bank_transfer' && expandedBank && option.details && (
                    <BankDetails>
                      {option.details.bankName && (
                        <BankRow>
                          <BankLabel>Bank</BankLabel>
                          <BankValue>{option.details.bankName}</BankValue>
                        </BankRow>
                      )}
                      {option.details.accountName && (
                        <BankRow>
                          <BankLabel>Account Name</BankLabel>
                          <BankValue>{option.details.accountName}</BankValue>
                        </BankRow>
                      )}
                      {option.details.accountNumber && (
                        <BankRow>
                          <BankLabel>Account Number</BankLabel>
                          <BankValue>{option.details.accountNumber}</BankValue>
                        </BankRow>
                      )}
                      {option.details.routingNumber && (
                        <BankRow>
                          <BankLabel>Routing Number</BankLabel>
                          <BankValue>{option.details.routingNumber}</BankValue>
                        </BankRow>
                      )}
                      {option.details.swiftCode && (
                        <BankRow>
                          <BankLabel>SWIFT/BIC</BankLabel>
                          <BankValue>{option.details.swiftCode}</BankValue>
                        </BankRow>
                      )}
                      {option.details.notes && (
                        <div style={{ marginTop: 12, fontSize: 13, color: '#65748d' }}>
                          📝 {option.details.notes}
                        </div>
                      )}
                    </BankDetails>
                  )}
                </div>
              ))}

              {/* Confirm Payment Button */}
              <ConfirmSection>
                <p style={{ fontSize: 13, color: '#7b8aa3', marginBottom: 12, textAlign: 'center' }}>
                  After completing your payment, click below to confirm
                </p>
                <ConfirmButton 
                  $confirmed={selectedMethod} 
                  onClick={handleConfirmPayment}
                  disabled={!selectedMethod || confirming}
                >
                  {confirming ? 'Confirming...' : selectedMethod ? '✓ I\'ve Completed Payment' : 'Select a payment method'}
                </ConfirmButton>
              </ConfirmSection>
            </>
          )}
        </Body>
      </PaymentCard>
    </PageContainer>
  );
};

export default PaymentPage;
